import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Tab,
  Tabs,
  Divider,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import { enNZ } from 'date-fns/locale'
import { DiscountCode } from '@/interfaces/discountCodes'
import ActiveDiscountsView from './ActiveDiscountsView'
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material'

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DiscountCodeRevisions from './DiscountCodeRevisions'

interface ShowDiscountCodeDialogProps {
  discountCode: DiscountCode
  open: boolean
  onClose: () => void
}

const getStatusChip = (code: DiscountCode) => {
  const now = new Date()
  const validFrom = new Date(code.valid_from)
  const validUntil = new Date(code.valid_until)

  if (!code.active) {
    return <Chip label="Inactive" color="default" size="small" />
  }

  if (now < validFrom) {
    return <Chip label="Scheduled" color="info" size="small" />
  }

  if (now > validUntil) {
    return <Chip label="Expired" color="warning" size="small" />
  }

  if (code.max_uses && code.current_uses >= code.max_uses) {
    return <Chip label="Exhausted" color="warning" size="small" />
  }

  return <Chip label="Active" color="success" size="small" />
}

export default function ShowDiscountCodeDialog({
  discountCode,
  open,
  onClose,
}: ShowDiscountCodeDialogProps) {
  const [tabValue, setTabValue] = useState(0)

  function a11yProps(index: number) {
    return {
      id: `discount-code-tab-${index}`,
      'aria-controls': `discount-code-tabpanel-${index}`,
    }
  }
  function handleTabChange(e: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue)
  }

  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleClose = () => {
    setErrors({})
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={tabValue === 1 ? 'xl' : 'md'}
      fullWidth
    >
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
      <DialogTitle sx={{ mt: -3 }}>
        {' '}
        Discount Code: {discountCode.code}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBotton: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Details" {...a11yProps(0)} />
            <Tab label="Usage" {...a11yProps(1)} />
            <Tab label="Revisions" {...a11yProps(2)} />
          </Tabs>
          <Divider />
        </Box>
        {tabValue == 0 && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: '#1C3045' }}>
                {getStatusChip(discountCode)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {discountCode.active ? (
                  <>
                    <CheckCircleOutlineIcon sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ color: '#1C3045' }}>
                      Active
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelOutlinedIcon sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ color: '#1C3045' }}>
                      Inactive
                    </Typography>
                  </>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {discountCode.archived ? (
                  <>
                    <VisibilityOffOutlined sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ color: '#1C3045' }}>
                      Archived
                    </Typography>
                  </>
                ) : (
                  <>
                    <VisibilityOutlined sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" sx={{ color: '#1C3045' }}>
                      Not Archived
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={enNZ}
            >
              <Box sx={{ pt: 2 }}>
                <Typography
                  variant="body1"
                  sx={{ color: '#1C3045', mt: 1, fontWeight: 600 }}
                >
                  Description
                </Typography>
                <Typography variant="body2" sx={{ color: '#1C3045', mb: 3 }}>
                  {discountCode.description}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#1C3045', mt: 1, fontWeight: 600 }}
                >
                  Details
                </Typography>
                <Typography variant="body2" sx={{ color: '#1C3045', mb: 0.5 }}>
                  {discountCode.discount_value}% for{' '}
                  {discountCode.duration_months} months
                </Typography>

                <Typography variant="body2" sx={{ color: '#1C3045', mb: 3 }}>
                  Valid from{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {' '}
                    {format(new Date(discountCode.valid_from), 'd MMM yyyy')}
                  </span>{' '}
                  to{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {format(new Date(discountCode.valid_until), 'd MMM yyyy')}
                  </span>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ color: '#1C3045', mt: 1, fontWeight: 600 }}
                >
                  Usage
                </Typography>
                <Typography variant="body2" sx={{ color: '#1C3045', mb: 1 }}>
                  {discountCode.current_uses} / {discountCode.max_uses}
                </Typography>
              </Box>
            </LocalizationProvider>
          </>
        )}
        {tabValue == 1 && (
          <ActiveDiscountsView discountCodeID={discountCode.id} />
        )}
        {tabValue == 2 && (
          <DiscountCodeRevisions discountCodeID={discountCode.id} />
        )}
      </DialogContent>
    </Dialog>
  )
}
